import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { downloadPdf } from "../../../lib/downloadPdf";

const API = process.env.REACT_APP_API_URL;

const localToday = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const rowKey = (row) => `${row.mode}-${row.payment_id}`;

const invoicePdfUrl = (row) => {
  if (!row?.invoice_ref) return "";
  if (row.mode === "video") {
    return `${API}/admin/video/pdf?video_invoice_ref=${encodeURIComponent(row.invoice_ref)}`;
  }
  if (!row.student_id) return "";
  return `${API}/admin/pdf?student_id=${encodeURIComponent(row.student_id)}&invoice_ref=${encodeURIComponent(row.invoice_ref)}`;
};

const slipUrl = (slipPath) => {
  if (!slipPath) return "";
  if (/^https?:\/\//i.test(slipPath)) return slipPath;
  return `${API}/${String(slipPath).replace(/^\/+/, "")}`;
};

const receiptPdfUrl = (row) => {
  if (!row?.invoice_ref) return "";
  if (row.mode === "video") {
    return `${API}/admin/video/receipts/pdf?video_invoice_ref=${encodeURIComponent(row.invoice_ref)}`;
  }
  return `${API}/admin/receipts/pdf?invoice_ref=${encodeURIComponent(row.invoice_ref)}`;
};

const stamp = () => new Date().toLocaleTimeString();

function PdfScrollBox({ title, src, loading, error }) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col border border-gray-200 bg-white">
      <div className="border-b px-3 py-2 text-sm font-medium text-gray-700">{title}</div>
      <div className="h-[560px] overflow-auto bg-gray-100">
        {loading && <p className="p-4 text-sm text-gray-500">Loading preview...</p>}
        {!loading && error && <p className="p-4 text-sm text-red-600">{error}</p>}
        {!loading && !error && !src && <p className="p-4 text-sm text-gray-500">No preview.</p>}
        {src && <iframe title={title} src={src} className="h-[1100px] w-full bg-white" />}
      </div>
    </div>
  );
}

function SlipScrollBox({ src }) {
  const isPdf = /\.pdf($|\?)/i.test(src || "");
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col border border-gray-200 bg-white">
      <div className="border-b px-3 py-2 text-sm font-medium text-gray-700">Slip</div>
      <div className="h-[560px] overflow-auto bg-gray-100">
        {!src && <p className="p-4 text-sm text-gray-500">No slip uploaded.</p>}
        {src && isPdf && <iframe title="Slip" src={src} className="h-[1100px] w-full bg-white" />}
        {src && !isPdf && <img src={src} alt="Payment slip" className="min-w-full bg-white" />}
      </div>
    </div>
  );
}

function RecordList({ rows, selected, onSelect, emptyText }) {
  return (
    <div className="h-[640px] overflow-y-auto border-r">
      {rows.length === 0 && <p className="p-4 text-sm text-gray-500">{emptyText}</p>}
      {rows.map((row) => {
        const active = selected && rowKey(row) === rowKey(selected);
        return (
          <button
            key={rowKey(row)}
            onClick={() => onSelect(row)}
            className={`block w-full border-b px-4 py-3 text-left ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900">{row.student_string || "Unknown"}</span>
              <span className="rounded bg-gray-200 px-2 py-0.5 text-xs uppercase">{row.mode}</span>
            </div>
            <div className="mt-1 text-sm text-gray-600">
              ${Number(row.expected_amount || 0).toFixed(2)} · {row.month || "-"}
            </div>
            <div className="truncate text-xs text-gray-500">{row.invoice_ref || "No invoice ref"}</div>
          </button>
        );
      })}
    </div>
  );
}

export default function PaymentOverview() {
  const [section, setSection] = useState("distribution");
  const [distribution, setDistribution] = useState([]);
  const [checking, setChecking] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [search, setSearch] = useState("");
  const [receivedDate, setReceivedDate] = useState(localToday);
  const [downloadReceipt, setDownloadReceipt] = useState(false);
  const [logText, setLogText] = useState("");
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [invoiceSrc, setInvoiceSrc] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const urlsRef = useRef([]);

  const addLog = (text) => {
    setLogText((prev) => {
      const line = `${stamp()} ${text}`;
      return prev.trim() ? `${prev.replace(/\s+$/, "")}\n${line}` : line;
    });
  };

  const clearPreview = () => {
    urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    urlsRef.current = [];
    setInvoiceSrc("");
  };

  const loadDistribution = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/pending-distribution`);
      const rows = res.data || [];
      setDistribution(rows);
      setSelectedKey((current) =>
        rows.some((row) => rowKey(row) === current) ? current : rows[0] ? rowKey(rows[0]) : null
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load pending distribution.");
    } finally {
      setLoading(false);
    }
  };

  const loadChecking = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/checking-payments`);
      const rows = res.data || [];
      setChecking(rows);
      setSelectedKey((current) =>
        rows.some((row) => rowKey(row) === current) ? current : rows[0] ? rowKey(rows[0]) : null
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load checking payments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearch("");
    setSelectedKey(null);
    if (section === "distribution") loadDistribution();
    else loadChecking();
  }, [section]);

  const sourceRows = section === "distribution" ? distribution : checking;
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sourceRows;
    return sourceRows.filter((row) => {
      const name = String(row.student_string || "").toLowerCase();
      const ref = String(row.invoice_ref || "").toLowerCase();
      return name.includes(term) || ref.includes(term);
    });
  }, [sourceRows, search]);
  const selected = filtered.find((row) => rowKey(row) === selectedKey) || filtered[0] || null;

  useEffect(() => {
    let cancelled = false;
    const loadPreview = async () => {
      clearPreview();
      setPreviewError("");
      if (!selected) return;
      const invoiceUrl = invoicePdfUrl(selected);
      if (!invoiceUrl) {
        setPreviewError("Missing invoice reference.");
        return;
      }
      setPreviewLoading(true);
      try {
        if (invoiceUrl) {
          const res = await axios.get(invoiceUrl, { responseType: "blob" });
          const objectUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
          } else {
            urlsRef.current.push(objectUrl);
            setInvoiceSrc(objectUrl);
          }
        }
      } catch (err) {
        if (!cancelled) setPreviewError(err.response?.data?.error || "Failed to load preview.");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [selected ? rowKey(selected) : "", section]);

  useEffect(() => () => clearPreview(), []);

  const selectRow = (row) => {
    setSelectedKey(rowKey(row));
    if (section === "verification") setReceivedDate(localToday());
  };

  const distribute = async () => {
    if (!selected || working) return;
    setWorking(true);
    setError("");
    try {
      await axios.post(`${API}/admin/pending-distribution/distribute`, {
        mode: selected.mode,
        payment_id: selected.payment_id,
      });
      const currentIndex = filtered.findIndex((row) => rowKey(row) === rowKey(selected));
      const next = filtered[currentIndex + 1] || filtered[currentIndex - 1] || null;
      setDistribution((prev) => prev.filter((row) => rowKey(row) !== rowKey(selected)));
      setSelectedKey(next ? rowKey(next) : null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to distribute invoice.");
    } finally {
      setWorking(false);
    }
  };

  const verify = async () => {
    if (!selected || working) return;
    setWorking(true);
    setError("");
    const label = `${selected.student_string || "Unknown"} ${selected.invoice_ref || ""}`.trim();
    try {
      await axios.post(`${API}/admin/checking-payments/verify`, {
        mode: selected.mode,
        payment_id: selected.payment_id,
        received_date: receivedDate,
      });
      addLog(`Verified ${label}`);
      if (downloadReceipt && selected.invoice_ref) {
        try {
          await downloadPdf(receiptPdfUrl(selected), `receipt_${selected.invoice_ref}.pdf`);
          addLog(`Downloaded receipt ${selected.invoice_ref}`);
        } catch (err) {
          addLog(`Receipt download failed for ${selected.invoice_ref}`);
          setError(err.response?.data?.error || "Verified, but receipt download failed.");
        }
      }
      const currentIndex = filtered.findIndex((row) => rowKey(row) === rowKey(selected));
      const next = filtered[currentIndex + 1] || filtered[currentIndex - 1] || null;
      setChecking((prev) => prev.filter((row) => rowKey(row) !== rowKey(selected)));
      setSelectedKey(next ? rowKey(next) : null);
      setReceivedDate(localToday());
    } catch (err) {
      const message = err.response?.data?.error || "Failed to verify payment.";
      setError(message);
      addLog(`Verify failed for ${label}: ${message}`);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="border border-gray-200 bg-white">
      <div className="border-b px-4 py-3">
        <h2 className="text-xl font-semibold text-gray-900">Verification Hub</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => setSection("distribution")}
            className={`rounded px-4 py-2 text-sm font-medium ${
              section === "distribution" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            Pending invoice distribution
          </button>
          <button
            onClick={() => setSection("verification")}
            className={`rounded px-4 py-2 text-sm font-medium ${
              section === "verification" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            Pending payment collect
          </button>
        </div>
      </div>
      {error && <div className="bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div>
          <div className="border-b p-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Student name or invoice number"
              className="w-full rounded border px-3 py-2"
            />
          </div>
          {loading ? (
            <p className="p-4 text-sm text-gray-500">Loading...</p>
          ) : (
            <RecordList
              rows={filtered}
              selected={selected}
              onSelect={selectRow}
              emptyText={section === "distribution" ? "No invoices waiting to distribute." : "No payments waiting to collect."}
            />
          )}
        </div>
        {!selected ? (
          <div className="flex h-[640px] items-center justify-center text-gray-500">Select a record</div>
        ) : section === "distribution" ? (
          <div className="flex min-w-0 flex-col gap-4 p-4">
            <PdfScrollBox title="Invoice" src={invoiceSrc} loading={previewLoading} error={previewError} />
            <div>
              <button
                onClick={distribute}
                disabled={working || !selected.invoice_ref}
                className="rounded bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {working ? "Distributing..." : "Distribute"}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid min-w-0 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px]">
            <div className="grid min-w-0 grid-cols-1 gap-3 p-4 md:grid-cols-2">
              <PdfScrollBox title="Invoice" src={invoiceSrc} loading={previewLoading} error={previewError} />
              <SlipScrollBox src={slipUrl(selected.slip_path)} />
            </div>
            <div className="border-l p-4">
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Student</dt>
                  <dd className="font-medium">{selected.student_string || "-"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Parent contact</dt>
                  <dd className="font-medium">
                    {[selected.parent_name, selected.parent_phone].filter(Boolean).join(" · ") || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Invoice #</dt>
                  <dd className="font-medium">{selected.invoice_ref || "-"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Expected amount</dt>
                  <dd className="font-medium">${Number(selected.expected_amount || 0).toFixed(2)}</dd>
                </div>
              </dl>
              <label className="mt-5 block text-sm text-gray-600">
                Date received
                <input
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                  className="mt-1 block rounded border px-3 py-2"
                />
              </label>
              <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={downloadReceipt}
                  onChange={(e) => setDownloadReceipt(e.target.checked)}
                />
                Download receipt after verify
              </label>
              <button
                onClick={verify}
                disabled={working || !selected.slip_path}
                className="mt-4 rounded bg-green-600 px-5 py-2.5 font-medium text-white hover:bg-green-700 disabled:opacity-60"
              >
                {working ? "Verifying..." : "Verify & Receipt"}
              </button>
              {!selected.slip_path && <p className="mt-2 text-xs text-gray-500">No uploaded slip on this payment.</p>}
              <div className="mt-4">
                <div className="mb-1 text-sm font-medium text-gray-700">Log</div>
                <textarea
                  value={logText}
                  onChange={(e) => setLogText(e.target.value)}
                  placeholder="Log"
                  className="h-40 w-full resize-y rounded border p-2 text-xs text-gray-700"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
